import {
	autoUpdate,
	flip,
	offset,
	shift,
	useFloating,
} from '@floating-ui/react';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faCheck,
	faChevronDown,
	faClock,
	faSearch,
	faSpinner,
	faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Combobox, Transition } from '@headlessui/react';
import { debounce } from 'lodash';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';

// Types
export interface SelectSearchOption {
	value: string | number;
	label: string;
	subtitle?: string;
	disabled?: boolean;
}

export interface SelectSearchGroup {
	key: string;
	label: string;
	icon?: IconDefinition;
	options: Array<SelectSearchOption>;
}

interface SelectSearchProps {
	// Core props
	value?: string | number | null;
	onChange?: (value: string) => void;
	placeholder?: string;
	disabled?: boolean;
	error?: boolean;
	fullWidth?: boolean;
	className?: string;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	clearable?: boolean;

	// Static mode props
	groups?: Array<SelectSearchGroup>;
	emptyMessage?: string;

	// Dynamic mode props
	mode?: 'static' | 'dynamic';
	loadRecent?: () => Promise<SelectSearchOption[]>;
	searchItems?: (query: string) => Promise<SelectSearchOption[]>;
	loadItemById?: (id: string) => Promise<SelectSearchOption | null>;
	staticGroups?: Array<SelectSearchGroup>; // Static groups to always show in dynamic mode

	// Dynamic mode configuration
	searchDebounceMs?: number;
	minSearchLength?: number;
	maxRecentItems?: number;

	// Messages
	loadingMessage?: string;
	searchingMessage?: string;
	emptyRecentMessage?: string;
	emptySearchMessage?: string;

	// Group labels for dynamic mode
	recentGroupLabel?: string;
	recentGroupIcon?: IconDefinition;
	searchGroupLabel?: string;
	searchGroupIcon?: IconDefinition;
}

// Ensure portal container exists
if (
	typeof window !== 'undefined' &&
	!document.getElementById('select-search-portal')
) {
	const portalDiv = document.createElement('div');
	portalDiv.id = 'select-search-portal';
	portalDiv.style.position = 'fixed';
	portalDiv.style.top = '0';
	portalDiv.style.left = '0';
	portalDiv.style.zIndex = '9999';
	portalDiv.style.pointerEvents = 'none';
	document.body.appendChild(portalDiv);
}

export const SelectSearch = React.forwardRef<
	HTMLInputElement,
	SelectSearchProps
>(
	(
		{
			value,
			onChange,
			placeholder = 'Search...',
			disabled = false,
			error = false,
			fullWidth = true,
			className,
			size = 'md',
			clearable = false,

			// Static mode
			groups = [],
			emptyMessage = 'No options found',

			// Dynamic mode
			mode = 'static',
			loadRecent,
			searchItems,
			loadItemById,
			staticGroups = [],

			// Configuration
			searchDebounceMs = 300,
			minSearchLength = 2,
			maxRecentItems = 10,

			// Messages
			loadingMessage = 'Loading...',
			searchingMessage = 'Searching...',
			emptyRecentMessage = 'No recent items',
			emptySearchMessage = 'No results found',

			// Group labels
			recentGroupLabel = 'Recent',
			recentGroupIcon = faClock,
			searchGroupLabel = 'Search Results',
			searchGroupIcon = faSearch,
		},
		ref,
	) => {
		const colors = useThemeColors();
		const { t } = useTranslation();

		// State
		const [selectedOption, setSelectedOption] =
			useState<SelectSearchOption | null>(null);
		const [query, setQuery] = useState('');
		const [recentItems, setRecentItems] = useState<SelectSearchOption[]>([]);
		const [searchResults, setSearchResults] = useState<SelectSearchOption[]>(
			[],
		);
		const [isLoadingRecent, setIsLoadingRecent] = useState(false);
		const [isSearching, setIsSearching] = useState(false);
		const [hasLoadedRecent, setHasLoadedRecent] = useState(false);
		const [lastSelectedOption, setLastSelectedOption] =
			useState<SelectSearchOption | null>(null);

		// Floating UI for dropdown positioning
		const { x, y, strategy, refs } = useFloating({
			placement: 'bottom-start',
			middleware: [offset(4), flip(), shift({ padding: 8 })],
			whileElementsMounted: autoUpdate,
		});

		// Size classes
		const sizeClasses = {
			xs: 'px-1.5 py-2 text-xs',
			sm: 'px-2.5 py-1.5 text-sm',
			md: 'px-3 py-2 text-sm',
			lg: 'px-4 py-2.5 text-base',
		}[size];

		// Build all available options for finding selected value
		const allStaticOptions = useMemo(
			() => groups.flatMap((group) => group.options),
			[groups],
		);

		// Create placeholder option memoized to prevent recreating on every render
		const placeholderOption = useMemo(() => {
			if (value != null) {
				return {
					value,
					label: String(value),
				};
			}
			return null;
		}, [value]);

		// Find and set selected option when value changes
		useEffect(() => {
			if (value != null) {
				// First check if we have a lastSelectedOption that matches this value
				if (
					lastSelectedOption &&
					String(lastSelectedOption.value) === String(value)
				) {
					setSelectedOption((current) => {
						// Only update if different
						if (
							current?.value === lastSelectedOption.value &&
							current?.label === lastSelectedOption.label
						) {
							return current;
						}
						return lastSelectedOption;
					});
					return;
				}

				// Otherwise check static groups
				let found = allStaticOptions.find(
					(opt) => String(opt.value) === String(value),
				);

				// If not found and in dynamic mode, check recent items
				if (!found && mode === 'dynamic') {
					found = recentItems.find(
						(opt) => String(opt.value) === String(value),
					);
				}

				// If not found and in dynamic mode, check search results
				if (!found && mode === 'dynamic') {
					found = searchResults.find(
						(opt) => String(opt.value) === String(value),
					);
				}

				// If still not found in dynamic mode, try loadItemById first, then search
				if (!found && mode === 'dynamic' && value) {
					if (loadItemById) {
						loadItemById(String(value))
							.then((item) => {
								if (item) {
									setSelectedOption(item);
									setLastSelectedOption(item);
								} else {
									// Only fall back to placeholder if loadItemById returns null
									setSelectedOption(placeholderOption);
								}
							})
							.catch((err) => {
								console.error('Failed to load item by ID:', err);
								// Fall back to placeholder on error
								setSelectedOption(placeholderOption);
							});

						// Return early - don't set placeholder immediately (wait for async)
						return;
					} else if (searchItems) {
						searchItems(String(value))
							.then((results) => {
								const matchingItem = results.find(
									(opt) => String(opt.value) === String(value),
								);

								if (matchingItem) {
									setSelectedOption(matchingItem);
									setLastSelectedOption(matchingItem);
								} else {
									setSelectedOption(placeholderOption);
								}
							})
							.catch((err) => {
								console.error('Failed to search for item by value:', err);
								setSelectedOption(placeholderOption);
							});

						// Return early - don't set placeholder immediately (wait for async)
						return;
					}
				}

				const newOption = found || placeholderOption;

				// Only update if the option actually changed
				setSelectedOption((current) => {
					if (
						current?.value === newOption?.value &&
						current?.label === newOption?.label
					) {
						return current;
					}

					return newOption;
				});
			} else {
				setSelectedOption(null);
				setLastSelectedOption(null);
			}
		}, [
			value,
			allStaticOptions,
			mode,
			recentItems,
			searchResults,
			placeholderOption,
		]); // Removed lastSelectedOption from deps

		// Load recent items in dynamic mode
		useEffect(() => {
			if (mode === 'dynamic' && loadRecent && !hasLoadedRecent && !disabled) {
				setIsLoadingRecent(true);
				loadRecent()
					.then((items) => {
						setRecentItems(items);
						setHasLoadedRecent(true);
					})
					.catch((err) => {
						console.error('Failed to load recent items:', err);
						setRecentItems([]);
					})
					.finally(() => {
						setIsLoadingRecent(false);
					});
			}
		}, [mode, loadRecent, hasLoadedRecent, disabled]);

		// Use a ref to hold the latest searchItems function so the debounced
		// wrapper stays stable across renders (searchItems changes reference
		// every render because its dependencies are defined inline).
		const searchItemsRef = useRef(searchItems);
		useEffect(() => {
			searchItemsRef.current = searchItems;
		}, [searchItems]);

		// Debounced search for dynamic mode
		const debouncedSearch = useMemo(
			() =>
				debounce(async (searchQuery: string) => {
					if (!searchItemsRef.current || searchQuery.length < minSearchLength) {
						setSearchResults([]);
						setIsSearching(false);
						return;
					}

					setIsSearching(true);
					try {
						const results = await searchItemsRef.current(searchQuery);
						setSearchResults(results);
					} catch (err) {
						console.error('Search failed:', err);
						setSearchResults([]);
					} finally {
						setIsSearching(false);
					}
				}, searchDebounceMs),
			[minSearchLength, searchDebounceMs],
		);

		// Trigger search when query changes
		useEffect(() => {
			if (mode === 'dynamic' && query) {
				void debouncedSearch(query);
			} else if (mode === 'dynamic') {
				setSearchResults([]);
				setIsSearching(false);
			}
		}, [mode, query, debouncedSearch]);

		// Build effective groups based on mode
		const effectiveGroups = useMemo(() => {
			if (mode === 'static') {
				// Static mode - use provided groups or filter by query
				if (!query) {
					return groups;
				}

				// Filter groups based on query
				return groups
					.map((group) => ({
						...group,
						options: group.options.filter((option) => {
							const searchText =
								`${option.label} ${option.subtitle || ''}`.toLowerCase();
							return searchText.includes(query.toLowerCase());
						}),
					}))
					.filter((group) => group.options.length > 0);
			} else {
				// Dynamic mode - build groups from static + recent/search results
				const dynamicGroups: SelectSearchGroup[] = [];

				// Add static groups first (if any)
				if (staticGroups.length > 0) {
					dynamicGroups.push(...staticGroups);
				}

				if (query && query.length >= minSearchLength) {
					// Show search results
					if (searchResults.length > 0) {
						dynamicGroups.push({
							key: 'search',
							label: searchGroupLabel,
							icon: searchGroupIcon,
							options: searchResults,
						});
					}
				} else {
					// Show recent items
					if (recentItems.length > 0) {
						dynamicGroups.push({
							key: 'recent',
							label: recentGroupLabel,
							icon: recentGroupIcon,
							options: recentItems.slice(0, maxRecentItems),
						});
					}
				}

				return dynamicGroups;
			}
		}, [
			mode,
			groups,
			query,
			recentItems,
			searchResults,
			minSearchLength,
			maxRecentItems,
			recentGroupLabel,
			recentGroupIcon,
			searchGroupLabel,
			searchGroupIcon,
			staticGroups,
		]);

		// Handle selection
		const handleChange = (option: SelectSearchOption | null) => {
			if (option) {
				setSelectedOption(option);
				setLastSelectedOption(option);
				onChange?.(String(option.value));
				setQuery(''); // Clear search query after selection
			}
		};

		// Close and blur after selection
		const handleOptionClick = (close: () => void) => {
			// Close the dropdown
			close();
			// Blur the input after a small delay to ensure state updates
			setTimeout(() => {
				if (document.activeElement instanceof HTMLElement) {
					document.activeElement.blur();
				}
			}, 10);
		};

		// Determine loading state
		const isLoading =
			mode === 'dynamic' ? isLoadingRecent || isSearching : false;

		return (
			<Combobox
				value={selectedOption}
				onChange={handleChange}
				disabled={disabled}
				by={(a, b) => String(a?.value) === String(b?.value)}
			>
				{({ open, close }) => (
					<div
						className={cn('relative', fullWidth ? 'w-full' : 'w-auto')}
						ref={refs.setReference}
					>
						<Combobox.Button as="div" className="relative">
							<Combobox.Input
								ref={ref}
								className={cn(
									'relative w-full text-left cursor-pointer',
									clearable && value ? 'pr-16' : 'pr-10',
									sizeClasses,
									colors.classes.formFieldBase,
									error
										? colors.classes.formFieldError
										: colors.classes.formFieldDefault,
									colors.classes.formFieldFocus,
									disabled && colors.classes.formFieldDisabled,
									fullWidth && 'w-full',
									className,
								)}
								placeholder={t(placeholder)}
								displayValue={(option: SelectSearchOption | null) =>
									option?.label ?? ''
								}
								onChange={(event) => {
									setQuery(event.target.value);
								}}
								onKeyDown={(event) => {
									event.stopPropagation();
								}}
								onFocus={(event) => {
									const input = event.target as HTMLInputElement;
									requestAnimationFrame(() => {
										input.select();
									});
								}}
								onBlur={() => {
									setQuery('');
								}}
								autoComplete="off"
							/>
							<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
								<FontAwesomeIcon
									icon={
										isLoading ? faSpinner : query ? faSearch : faChevronDown
									}
									className={cn(
										'h-3 w-3',
										colors.classes.textSecondary,
										isLoading && 'animate-spin',
									)}
									aria-hidden="true"
								/>
							</div>
						</Combobox.Button>
						{clearable && value != null && String(value) !== '' && (
							<button
								type="button"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									onChange?.('');
									setSelectedOption(null);
									setQuery('');
								}}
								className={cn(
									'absolute right-6 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center cursor-pointer',
								)}
								disabled={disabled}
							>
								<FontAwesomeIcon
									icon={faTimes}
									className={cn(
										'h-3 w-3',
										colors.classes.textSecondary,
										'hover:' + colors.classes.textPrimary,
										'transition-colors',
									)}
									aria-hidden="true"
								/>
							</button>
						)}

						{open &&
							document.getElementById('select-search-portal') &&
							createPortal(
								<div
									style={{ pointerEvents: 'auto' }}
									data-select-search-dropdown="true"
								>
									<Transition
										as={Fragment}
										show={open}
										leave="transition ease-in duration-100"
										leaveFrom="opacity-100"
										leaveTo="opacity-0"
										afterLeave={() => {
											setQuery('');
										}}
									>
										<Combobox.Options
											ref={refs.setFloating}
											style={{
												position: strategy,
												top: y ?? 0,
												left: x ?? 0,
												width: refs.reference.current?.offsetWidth,
												pointerEvents: 'auto',
											}}
											className={cn(
												'z-[9999] max-h-60 overflow-auto custom-scrollbar rounded-md py-1 text-base sm:text-sm shadow-lg',
												colors.classes.selectMenu,
											)}
										>
											{isLoading ? (
												<div
													className={cn(
														'relative cursor-default py-2 px-3 text-center',
														colors.classes.textSecondary,
													)}
												>
													<FontAwesomeIcon
														icon={faSpinner}
														className="animate-spin mr-2"
													/>
													{t(
														mode === 'dynamic' && !query
															? loadingMessage
															: searchingMessage,
													)}
												</div>
											) : effectiveGroups.length === 0 ? (
												<div
													className={cn(
														'relative cursor-default py-2 px-3 text-center',
														colors.classes.textSecondary,
													)}
												>
													{t(
														mode === 'dynamic'
															? query && query.length >= minSearchLength
																? emptySearchMessage
																: emptyRecentMessage
															: emptyMessage,
													)}
												</div>
											) : (
												effectiveGroups.map((group) => (
													<div key={group.key}>
														{/* Group Header */}
														{group.label && (
															<div
																className={cn(
																	'px-3 py-2 text-xs font-semibold uppercase tracking-wide flex items-center gap-2',
																	colors.classes.bgSecondary,
																	colors.classes.textSecondary,
																)}
															>
																{group.icon && (
																	<FontAwesomeIcon
																		icon={group.icon}
																		className="h-3 w-3"
																	/>
																)}
																{t(group.label)}
															</div>
														)}

														{/* Group Options */}
														{group.options.map((option, index) => (
															<Combobox.Option
																key={`${group.key}-${option.value ?? index}`}
																value={option}
																disabled={option.disabled}
																onClick={() => handleOptionClick(close)}
																className={({ active, selected }) =>
																	cn(
																		'relative py-2 pl-10 pr-4 transition-colors',
																		option.disabled
																			? 'cursor-not-allowed opacity-50'
																			: 'cursor-pointer',
																		selected &&
																			!active &&
																			colors.classes.selectOptionSelected,
																		active && colors.classes.selectOption,
																		!selected &&
																			!active &&
																			colors.classes.textPrimary,
																	)
																}
															>
																{({ selected }) => (
																	<>
																		<div>
																			<span
																				className={cn(
																					'block truncate',
																					selected
																						? 'font-medium'
																						: 'font-normal',
																				)}
																			>
																				{t(option.label)}
																			</span>
																			{option.subtitle && (
																				<span
																					className={cn(
																						'block truncate text-xs mt-0.5',
																						colors.classes.textSecondary,
																					)}
																				>
																					{t(option.subtitle)}
																				</span>
																			)}
																		</div>
																		{selected && (
																			<span
																				className={cn(
																					'absolute inset-y-0 left-0 flex items-center pl-3',
																					colors.classes.textPrimary,
																				)}
																			>
																				<FontAwesomeIcon
																					icon={faCheck}
																					className="h-3 w-3"
																					aria-hidden="true"
																				/>
																			</span>
																		)}
																	</>
																)}
															</Combobox.Option>
														))}
													</div>
												))
											)}
										</Combobox.Options>
									</Transition>
								</div>,
								document.getElementById('select-search-portal'),
							)}
					</div>
				)}
			</Combobox>
		);
	},
);

SelectSearch.displayName = 'SelectSearch';
